import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../mendepartmentdetails/Sidebar';
import SubDeptImage from '../../../assets/image/department/vehicle-registration.jpg';

function VehicleRegistrationDetails() {
  const [Faq, setFaq] = useState(0); // first dropdown open by default
  const titleRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');

  useEffect(() => {
    if (titleRef.current) {
      const c = window.getComputedStyle(titleRef.current).color;
      setTitleColor(c);
    }
  }, []);

  // shared inline styles (consistent with your other extended pages)
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.35, color: '#0f172a' };
  const valText = { fontSize: '1rem', lineHeight: 1.6, color: '#334155' };
  const overviewText = { color: titleColor || '#0f172a', fontSize: '1.05rem', lineHeight: 1.6, margin: '8px 0 16px' };

  const sections = [
    {
      Que: 'How to Register',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['New vehicle (dealer):', 'Dealer typically submits title/registration paperwork. You pay taxes/fees and receive a temporary tag if issued. Plates/registration arrive after processing.'],
            ['Private-party purchase:', 'Bring signed title, bill of sale, odometer disclosure (if required), proof of insurance, ID, and payment for taxes/fees. Complete title/registration forms at the counter.'],
            ['Out-of-state vehicle:', 'Provide out-of-state title/registration, proof of insurance, ID, and any inspection/emissions documents if applicable.'],
          ].map(([k, v], i) => (
            <li key={`how-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Renewals & Deadlines',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Renewal notice:', 'Check your mailed/email notice for due date and eligibility to renew online, by mail, or in person.'],
            ['Avoid lapses:', 'Expired registration can lead to citations. Renew before the due date to stay compliant.'],
            ['Address up to date:', 'Make sure your address is current so renewal notices reach you.'],
          ].map(([k, v], i) => (
            <li key={`renew-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Title, Transfers & Name Changes',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Ownership transfer:', 'Seller signs the title to the buyer. Submit title, proof of insurance, ID, and fees to retitle/register.'],
            ['Lien release:', 'If applicable, include a lien release or electronic lien satisfaction before retitling.'],
            ['Name change:', 'Provide legal proof of name change and update your title/registration records promptly.'],
          ].map(([k, v], i) => (
            <li key={`title-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Fees & Taxes',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Registration & title fees:', 'Base registration, title, and plate fees apply; local fees may also apply.'],
            ['Sales/use tax:', 'Collected on purchases based on the purchase price and applicable local rules.'],
            ['Other charges:', 'Duplicate documents, specialty plates, and temporary permits may carry additional fees.'],
          ].map(([k, v], i) => (
            <li key={`fees-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Insurance & Compliance',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Proof of insurance:', 'Active liability insurance is required to register and operate a vehicle.'],
            ['Continuous coverage:', 'Keep coverage active; lapses can trigger penalties or registration issues.'],
            ['SR-22 (if required):', 'Some drivers must maintain an SR-22 filing per court/DMV direction.'],
          ].map(([k, v], i) => (
            <li key={`ins-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Emissions/Inspection (If Applicable)',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Check requirements:', 'Some areas/vehicles require emissions or safety inspections at registration/renewal.'],
            ['Bring proof:', 'Provide a passing certificate or electronic proof if your area uses it.'],
            ['Exemptions:', 'Certain vehicles (e.g., antique, new model years) may qualify for exemptions where allowed.'],
          ].map(([k, v], i) => (
            <li key={`emi-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Plates, Tags & Temporary Permits',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Standard & specialty plates:', 'Choose standard plates or request specialty/organization designs if available.'],
            ['Temporary tags:', 'Used while permanent plates/registration are processed when eligibility allows.'],
            ['Disabled plates/placards:', 'Apply with required medical certification and documentation.'],
          ].map(([k, v], i) => (
            <li key={`plates-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Address Updates & Replacements',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Address change:', 'Update your address promptly so notices and decals reach you.'],
            ['Lost/stolen items:', 'Replace lost registration cards, decals, or plates. Report stolen plates to law enforcement.'],
            ['Duplicates:', 'Request duplicate title/registration if originals are damaged or unavailable.'],
          ].map(([k, v], i) => (
            <li key={`upd-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Commercial & Fleet (Overview)',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Weight-based fees:', 'Commercial vehicles may be assessed by gross vehicle weight and usage.'],
            ['Apportioned registration:', 'Interstate carriers may need IRP/IFTA where applicable.'],
            ['Fleet coordination:', 'Ask about bulk transactions or scheduled appointments for multiple units.'],
          ].map(([k, v], i) => (
            <li key={`com-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'After You Register',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Display & decals:', 'Install plates and affix the registration decal as directed.'],
            ['Keep proof in vehicle:', 'Carry your registration card; some areas permit digital proof.'],
            ['Verify info:', 'Confirm VIN, plate, and address on your documents are correct.'],
          ].map(([k, v], i) => (
            <li key={`after-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
  ];

  return (
    <section className="department-details-section">
      <div className="container">
        <div className="row">

          {/* Sidebar */}
          <div className="col-12 col-lg-4 col-xl-4">
            <Sidebar />
          </div>

          {/* Main Content */}
          <div className="col-lg-8">
            <div className="department-details-imgbox">
              <img src={SubDeptImage} className="img-fluid" alt="Vehicle Registration" />
            </div>

            <div className="department-details-content-box">
              <h4 ref={titleRef} className="department-details-title">Vehicle Registration</h4>
              {/* Overview (always visible, same color as title but smaller) */}
              <p style={overviewText}>
                Register your vehicle to stay legal on the road. Bring required ownership documents, proof of insurance,
                and payment for taxes/fees. Many renewals can be completed online if eligible.
              </p>
            </div>

            {/* Actions: keep fees PDF, move applications to Forms hub */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', margin: '0 0 16px' }}>
              <Link
                to="/forms/title-registration"
                className="btn btn-secondary d-inline-flex align-items-center justify-content-center"
              >
                Titles & Registrations (Online Forms)
              </Link>
            </div>

            {/* Accordion (expanded information) */}
            <div className="accordian-box">
              {sections.map((item, index) => (
                <div className={`accordian-box-item ${Faq !== index && 'active'}`} key={index}>
                  <div className="accordian-title">
                    <h5>{item.Que}</h5>
                    <i
                      className="fa-solid fa-angle-right"
                      onClick={() => setFaq(index)}
                      role="button"
                      aria-label={`Toggle ${item.Que}`}
                    />
                  </div>

                  {Faq === index && (
                    <div className="accordian-content" style={{ display: 'block' }}>
                      {item.Answer}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="department-details-policy-box">
              <h4>Our Commitment:</h4>
              <p>
                We provide clear steps and multiple options to register, renew, and update records—so you can stay
                compliant with minimal hassle.
              </p>
            </div>

            <div>
              <Link to="/department/dmv">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: '5px' }} />
                Go Back
              </Link>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

export default VehicleRegistrationDetails;
